import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  Card, SectionCard, StatusBadge, Button, PageLoader, EmptyState, Badge,
  Avatar, ExpiryCell, BackButton, formatDate, formatINR, cx,
  siteTimeline, relativeDate, activityTone,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Ruler, MapPin, Users, Truck, Telescope, Building2, CalendarDays, Activity, TriangleAlert, ExternalLink } from "lucide-react";

const daysLeft = (d) => {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((new Date(d) - today) / 86400000);
};

export default function AdminSiteDetail() {
  const { id } = useParams();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/sites/admin/${id}`)
      .then(({ data }) => setSite(data.data))
      .catch(() => toast.error("Failed to load site"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <AdminLayout><PageLoader label="Loading site…" /></AdminLayout>;
  if (!site) {
    return (
      <AdminLayout>
        <Card><EmptyState icon={<MapPin size={22} />} title="Site not found"
          description="This site may have been deleted."
          action={<Button to="/admin/sites" variant="secondary">Back to Sites</Button>} /></Card>
      </AdminLayout>
    );
  }

  const address = [site.address, site.city, site.state, site.country].filter(Boolean).join(", ");
  const hasGPS = site.coordinates?.lat != null && site.coordinates?.lng != null;
  const timeline = siteTimeline(site);
  const activity = site.activity;

  return (
    <AdminLayout>
      <div className="space-y-5">

        {/* ══ Header ══ */}
        <div className="flex flex-col gap-4">
          <nav className="flex items-center gap-2 text-xs text-ink-400">
            <Link to="/admin/sites" className="hover:text-ink-700 transition-colors">Sites</Link>
            <span>/</span>
            <span className="text-ink-600 font-medium truncate">{site.name}</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <BackButton to="/admin/sites" />
              <div className="min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-xl sm:text-2xl font-bold text-ink-900 font-display truncate">{site.name}</h1>
                  {site.code && <span className="text-xs text-ink-600 font-mono bg-ink-100 border border-ink-200 px-2 py-1 rounded-md">{site.code}</span>}
                  <StatusBadge status={site.status} />
                </div>
                {address && (
                  <p className="text-ink-400 text-sm mt-1.5 flex items-start gap-1.5">
                    <MapPin size={14} className="mt-0.5 shrink-0" /><span>{address}</span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {hasGPS && (
                <Button variant="secondary" href={`https://www.google.com/maps?q=${site.coordinates.lat},${site.coordinates.lng}`}
                  icon={<ExternalLink size={15} />} className="flex-1 sm:flex-none">Map</Button>
              )}
              <Button to={`/admin/sites/${site._id}/drawings`} icon={<Ruler size={15} />} className="flex-1 sm:flex-none">Drawings</Button>
            </div>
          </div>
        </div>

        {/* ══ Overview ══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SectionCard title="Client" icon={<Building2 size={16} />} bodyClass="p-4">
            {site.clientId ? (
              <div className="flex items-start gap-3">
                <Avatar name={site.clientId.name} tone="violet" size="md" />
                <div className="min-w-0">
                  <div className="text-ink-900 font-semibold truncate">{site.clientId.name}</div>
                  {site.clientId.company && <div className="text-ink-500 text-sm truncate">{site.clientId.company}</div>}
                  {site.clientId.email && (
                    <a href={`mailto:${site.clientId.email}`} className="block text-xs text-brand-600 hover:text-brand-700 mt-1 truncate">{site.clientId.email}</a>
                  )}
                  {site.clientId.phone && (
                    <a href={`tel:${site.clientId.phone}`} className="block text-xs text-ink-400 hover:text-brand-600">{site.clientId.phone}</a>
                  )}
                </div>
              </div>
            ) : <p className="text-ink-400 text-sm">No client assigned</p>}
          </SectionCard>

          <SectionCard title="Timeline" icon={<CalendarDays size={16} />} bodyClass="p-4">
            <div className="mb-3"><Badge variant={timeline.tone} dot>{timeline.label}</Badge></div>
            <dl className="space-y-2.5 text-sm">
              {[
                ["Start", site.startDate],
                ["End", site.endDate],
                ["GPS pinned", site.locationAddedAt],
                ["Created", site.createdAt],
                ["Updated", site.updatedAt],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="text-ink-500 shrink-0">{label}</dt>
                  <dd className="text-ink-800 font-medium text-right min-w-0">
                    {val ? (
                      <>
                        <span className="block truncate">{formatDate(val)}</span>
                        <span className="block text-[11px] text-ink-400 font-normal">{relativeDate(val)}</span>
                      </>
                    ) : "—"}
                  </dd>
                </div>
              ))}
            </dl>
          </SectionCard>

          {/* What has actually happened here — from the API's activity rollup */}
          <SectionCard title="Activity" icon={<Activity size={16} />} bodyClass="p-4">
            {activity?.lastActivityDate ? (
              <>
                <div className="mb-3">
                  <Badge variant={activityTone(activity.lastActivityDate)} dot>
                    Last active {relativeDate(activity.lastActivityDate)}
                  </Badge>
                </div>
                <dl className="space-y-2.5 text-sm">
                  {[
                    ["Attendance", activity.attendanceDays ? `${activity.attendanceDays} day${activity.attendanceDays > 1 ? "s" : ""}` : "—",
                      activity.lastAttendanceDate && `last ${relativeDate(activity.lastAttendanceDate)}`],
                    ["Expenses", activity.expenseCount ? formatINR(activity.expenseTotal, { compact: true }) : "—",
                      activity.lastExpenseDate && `last ${relativeDate(activity.lastExpenseDate)}`],
                    ["Drawings", activity.drawingCount || "—",
                      activity.lastDrawingAt && `last ${relativeDate(activity.lastDrawingAt)}`],
                  ].map(([label, val, sub]) => (
                    <div key={label} className="flex justify-between gap-3">
                      <dt className="text-ink-500 shrink-0">{label}</dt>
                      <dd className="text-ink-800 font-medium text-right min-w-0">
                        <span className="block truncate">{val}</span>
                        {sub && <span className="block text-[11px] text-ink-400 font-normal truncate">{sub}</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
              </>
            ) : (
              <p className="text-ink-400 text-sm">
                Nothing logged here yet — no attendance, approved expenses or drawings.
              </p>
            )}
          </SectionCard>
        </div>

        <SectionCard title="Description" bodyClass="p-4">
          <p className="text-ink-600 text-sm leading-relaxed whitespace-pre-wrap">
            {site.description || <span className="text-ink-300">No description provided</span>}
          </p>
        </SectionCard>

        {/* ══ Crew ══ */}
        <SectionCard title={`Assigned Crew (${site.assignedCrews?.length || 0})`} icon={<Users size={16} />} bodyClass="p-4">
          {site.assignedCrews?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {site.assignedCrews.map((crew) => (
                <div key={crew._id} className="flex items-center gap-3 bg-ink-50 rounded-xl p-3">
                  <Avatar name={crew.name} tone="emerald" size="sm" />
                  <div className="min-w-0">
                    <div className="text-ink-800 text-sm font-medium truncate">{crew.name}</div>
                    <div className="text-ink-400 text-xs truncate">{crew.role || crew.email}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-ink-400 text-sm">No crew assigned</p>}
        </SectionCard>

        {/* ══ Vehicles ══ */}
        <SectionCard title={`Assigned Vehicles (${site.assignedVehicles?.length || 0})`} icon={<Truck size={16} />} bodyClass="p-4">
          {site.assignedVehicles?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {site.assignedVehicles.map((v) => {
                const ins = daysLeft(v.insuranceExpiry);
                const pol = daysLeft(v.pollutionExpiry);
                const alert = (ins !== null && ins <= 30) || (pol !== null && pol <= 30);
                return (
                  <div key={v._id} className={cx("rounded-xl p-3 border", alert ? "bg-red-50 border-red-200" : "bg-ink-50 border-ink-100")}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-ink-800 text-sm font-semibold truncate">{v.name}</div>
                        {v.registrationNumber && <div className="font-mono text-[11px] text-ink-500 mt-0.5">{v.registrationNumber}</div>}
                        <div className="text-ink-400 text-xs capitalize mt-0.5 truncate">
                          {[v.type, v.make, v.model].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      {alert && <TriangleAlert size={15} className="text-amber-500 shrink-0" />}
                    </div>
                    {(v.insuranceExpiry || v.pollutionExpiry) && (
                      <div className="mt-2.5 pt-2.5 border-t border-ink-200/60 grid grid-cols-2 gap-2">
                        {v.insuranceExpiry && <ExpiryCell date={v.insuranceExpiry} label="Insurance" />}
                        {v.pollutionExpiry && <ExpiryCell date={v.pollutionExpiry} label="PUC" />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : <p className="text-ink-400 text-sm">No vehicles assigned</p>}
        </SectionCard>

        {/* ══ Instruments ══ */}
        <SectionCard title={`Assigned Instruments (${site.assignedInstruments?.length || 0})`} icon={<Telescope size={16} />} bodyClass="p-4">
          {site.assignedInstruments?.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {site.assignedInstruments.map((i) => (
                <div key={i._id} className="bg-ink-50 rounded-xl p-3">
                  <div className="text-ink-800 text-sm font-medium truncate">{i.name}</div>
                  <div className="text-ink-400 text-xs truncate">{[i.serialNumber, i.type].filter(Boolean).join(" · ") || "—"}</div>
                  {i.make && <div className="text-ink-400 text-xs truncate">{i.make} {i.model}</div>}
                </div>
              ))}
            </div>
          ) : <p className="text-ink-400 text-sm">No instruments assigned</p>}
        </SectionCard>

        {/* ══ Bills ══ */}
        {site.bills?.length > 0 && (
          <SectionCard title={`Bills (${site.bills.length})`}
            action={<Link to={`/admin/bills?siteId=${site._id}`} className="text-xs font-semibold text-brand-600 hover:text-brand-700">View all →</Link>}
            bodyClass="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {site.bills.map(b => (
                <Link key={b._id} to={`/admin/bills/${b._id}`}
                  className="flex items-center justify-between gap-3 bg-ink-50 hover:bg-ink-100 rounded-xl p-3 transition-colors">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-ink-700 truncate">{b.billNumber}</div>
                    <div className="text-[11px] text-ink-400">{formatDate(b.billDate)}</div>
                  </div>
                  <StatusBadge status={b.status} />
                </Link>
              ))}
            </div>
          </SectionCard>
        )}
      </div>
    </AdminLayout>
  );
}
